import { useState } from 'react';
import {
  IconArrowLeft, IconCalendar, IconChevronDown, IconFile, IconInfoCircle, IconLayoutSidebarRight, IconLock, IconMail, IconMaximize,
  IconRotateClockwise, IconSparkles, IconTag, IconUser, IconWeight, IconFileText, IconPhoto,
} from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import './i18n';
import { Page, useLayout } from '@hana/shell';
import { ViewerPhone } from './ViewerPhone';
import { Button, Chip, Input, Modal, Skeleton, StateView, Textarea, useToast, type ScreenState } from '@hana/ui';
import {
  ASK, ASK_CHIPS, CTX, OCR, GAL_COLORS, GAL_N, JOBFILES, JOBS, OFF_CONT, OFF_HEAD, OFF_PL, PDF_BC, TXT,
  askAnswer, badge, galItem, type PdfPage, type ViewKind,
} from './viewerData';
import { SignBar, SignPanel, StampMark, defaultSign, type SignState } from './pdfSign';
import { EditorBar, LeaveModal, PdfEditorBody, emptyEd, pickPages, usePdfEditor } from './pdfEditor';
import s from './viewer.module.css';

// Mint-FileViewer (1536 / 1920), chế độ XEM: thanh đầu 60 · cột thông tin 300 · vùng xem (thanh zoom 48 + giấy) · cột phải "HANA đọc tệp".
// Chế độ sửa PDF (mode=edit, pdfEditor.tsx) dựng theo board; ký / đóng dấu, đổi tên, gộp tệp, gửi email: chưa mở ghi — mọi nút ghi báo toast "Chưa mở ghi ở bản này".
// Dùng chung: /ezy/files/view?kind=pdf|image|office|text|mail|gallery&f=<chỉ số tệp job>&state=…

const KINDS: readonly ViewKind[] = ['pdf', 'image', 'office', 'text', 'mail', 'gallery'];
const BADGE_CLS = { PDF: s.bPdf, XLS: s.bXls, IMG: s.bImg, TXT: s.bTxt } as const;
type Grp = 'file' | 'send' | 'pdf' | 'job';
const GROUPS: readonly Grp[] = ['file', 'send'];
const MENUS: Record<Grp, readonly string[]> = {
  file: ['rename', 'dl', 'share', 'link', 'swap', 'del'],
  send: ['mail', 'attach', 'print'],
  pdf: ['editor', 'order', 'merge', 'sign', 'extract'],
  job: ['open', 'attach', 'task'],
};

function readParams() {
  const q = new URLSearchParams(location.search);
  const k = q.get('kind') as ViewKind | null;
  const st = q.get('state');
  const f = Number(q.get('f') ?? NaN);
  return {
    kind: k && KINDS.includes(k) ? k : ('pdf' as ViewKind),
    edit: q.get('mode') === 'edit',
    sign: q.get('mode') === 'sign',
    f: Number.isInteger(f) && f >= 0 && f < JOBFILES.length ? f : null,
    state: (st === 'loading' || st === 'empty' || st === 'error' || st === 'noperm' ? st : 'ready') as ScreenState,
  };
}

function Paper({ pg, no, total, name, z, rot = 0, stamp }: { pg: PdfPage; no: number; total: number; name: string; z: number; rot?: number; stamp?: SignState }) {
  const t = useT();
  const w = Math.round((560 * z) / 100);
  return (
    <article aria-label={t('fv.pageN', { n: no })} className={s.paper} style={{ transform: rot ? `rotate(${rot}deg)` : undefined, width: w, minHeight: Math.round((792 * z) / 100), padding: `${Math.round((40 * z) / 100)}px ${Math.round((48 * z) / 100)}px 60px`, fontSize: Math.max(10, Math.round((12 * z) / 100)) }}>
      <div className={s.pHead}>
        <div><div className={s.pTitle}>{pg.head}</div><div className={s.pSub}>{pg.sub}</div></div>
        <div className={s.pRight}>{pg.right}</div>
      </div>
      {pg.rows.length > 0 && (
        <div className={s.pRows}>{pg.rows.map(([k, v]) => <div key={k} className={s.pRow}><span>{k}</span><b>{v}</b></div>)}</div>
      )}
      <div className={s.pPara}>{pg.para.map((x) => <p key={x}>{x}</p>)}</div>
      {stamp && <StampMark v={stamp} />}
      <div className={s.pFoot}>{t('fv.pageFoot', { name, no, total })}</div>
    </article>
  );
}

export function ViewerPage() {
  return useLayout() === 'phone' ? <ViewerPhone state={readParams().state} /> : <ViewerDesktop />;
}

function ViewerDesktop() {
  const t = useT();
  const toast = useToast();
  const p0 = readParams();
  const [kind0] = useState(p0.kind);
  const [fi, setFi] = useState(p0.f ?? (kind0 === 'office' ? 2 : kind0 === 'text' ? 4 : 0));
  const [info, setInfo] = useState(true);
  const [side, setSide] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [sheet, setSheet] = useState<'PL' | 'CONT'>('PL');
  const [gi, setGi] = useState(0);
  const [rot, setRot] = useState(0);
  const [openG, setOpenG] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [sideTab, setSideTab] = useState<'ask' | 'ocr'>('ask');
  const [ocrRun, setOcrRun] = useState(false);
  const [log, setLog] = useState<[boolean, string][]>([]);
  const [dlg, setDlg] = useState<null | 'rename' | 'mail' | 'merge'>(null);
  const [mOff, setMOff] = useState<Record<number, boolean>>({});
  const [mTo, setMTo] = useState('');
  const [mode, setMode] = useState<'view' | 'edit' | 'sign'>(p0.edit ? 'edit' : p0.sign ? 'sign' : 'view');
  const [sg, setSg] = useState<SignState>(defaultSign(2));
  const [signed, setSigned] = useState<SignState | null>(null);
  const [leave, setLeave] = useState(false);
  const [saved, setSaved] = useState<ReturnType<typeof emptyEd> | null>(null);
  const editor = usePdfEditor(PDF_BC);
  const openEditor = () => { setOpenG(null); editor.reset(emptyEd(pages)); setMode('edit'); };
  const menuPick = (g: Grp, m: string) => {
    if (g === 'pdf' && (m === 'editor' || m === 'order')) { openEditor(); return; }
    if (g === 'pdf' && m === 'sign') { setOpenG(null); setSg(defaultSign(pages.length)); setMode('sign'); return; }
    if (g === 'file' && m === 'rename') { setOpenG(null); setDlg('rename'); return; }
    if (g === 'send' && m === 'mail') { setOpenG(null); setDlg('mail'); return; }
    if (g === 'pdf' && m === 'merge') { setOpenG(null); setDlg('merge'); return; }
    noWrite();
  };
  const doMerge = () => {
    const add = [0, 1].filter((i) => !mOff[i]);
    setDlg(null);
    if (mode !== 'edit') { editor.reset(emptyEd(pages)); setMode('edit'); }
    if (add.length) editor.apply((n) => add.forEach((i) => n.pages.push({ src: 100 + i, rot: 0 })), t('fv.op.merge', { n: add.length }));
  };
  const edCancel = () => (editor.ed.ops.length ? setLeave(true) : setMode('view'));
  const edSave = () => {
    if (!editor.ed.ops.length) return;
    setSaved(editor.ed); setMode('view'); editor.reset(editor.ed);
    toast({ msg: t('fv.savedEd', { f: `${name.replace(/\.pdf$/i, '')}_edited.pdf`, n: editor.ed.ops.length }) });
  };
  const dlgDone = () => { setDlg(null); toast({ msg: t('fl.noWrite') }); };
  const noWrite = () => { setOpenG(null); toast({ msg: t('fl.noWrite') }); };

  const inJob = kind0 === 'pdf' || kind0 === 'office' || kind0 === 'text';
  const jf = JOBFILES[fi] ?? JOBFILES[0]!;
  const kind: ViewKind = inJob ? jf.kind : kind0;
  const F = inJob ? { ...jf, job: 'SE260639142' } : { ...CTX[kind0 as 'image' | 'mail' | 'gallery'], locked: false };
  const gal = galItem(gi);
  const pdfK = kind === 'pdf' || kind === 'mail';
  const pages = pickPages(kind === 'mail' ? 'mail' : 'pdf', jf.code === 'SI' && inJob ? 'SI' : 'BKG');
  const name = kind === 'gallery' ? gal.n : F.n;
  const bd = badge(name, kind);
  const state = p0.state;
  const editing = mode === 'edit' && pdfK;
  const signing = mode === 'sign' && pdfK;
  const signedName = `${name.replace(/\.pdf$/i, '')}_signed.pdf`;
  const sigApply = () => { setSigned(sg); setSaved(null); setMode('view'); toast({ msg: t('fv.savedSign', { f: signedName, x: sg.type === 'ceo' ? t('fv.signCeo') : sg.type === 'surr' ? t('fv.signSurr') : t('fv.signDavid'), p: sg.page + 1 }) }); };
  const ready = state === 'ready';
  const job = JOBS[F.job];
  const wide = typeof window !== 'undefined' && window.matchMedia('(min-width: 1800px)').matches;
  const showSide = ready && (wide || side);
  const hasPages = pdfK;
  const ocrHow = pdfK ? t('fv.extract') : t('fv.ocrImg');

  const meta: [typeof IconFile, string, string][] = (kind === 'gallery'
    ? [[IconFile, gal.n, t('fv.file')], [IconPhoto, t('fv.nPhotos', { a: gi + 1, b: GAL_N }), t('fv.photo')], [IconUser, gal.by, t('fv.uploader')]]
    : [[IconFile, name, t('fv.file')], [IconTag, F.type, t('fv.type')], [IconUser, F.by, t('fv.uploader')], [IconCalendar, F.at.replace('/2026', ''), t('fv.uploadedAt', { at: F.at })], [IconWeight, F.size, t('fl.size')]]
  ).filter((x) => x[1]) as [typeof IconFile, string, string][];
  const fileRows: [string, string][] = kind === 'gallery'
    ? [[t('fv.photo'), gal.n], [t('fv.uploaderS'), gal.by], [t('fv.aiScore'), gal.sc], [t('fv.position'), `${gi + 1} / ${GAL_N}`]]
    : [[t('fv.typeDoc'), F.type], ['DocCode', F.code], [t('fv.upload'), `${F.by} · ${F.at}`], [t('fl.size'), F.size], [t('fv.savedAt'), kind === 'mail' ? t('fv.inMail') : t('fv.jobLib')], [t('fv.noDelete'), F.locked ? t('fv.yesDND') : t('fv.no')]];
  const title = kind === 'gallery' ? CTX.gallery.title : F.title || name;
  const backK = kind === 'gallery' ? t('fv.backAlbum') : kind === 'mail' ? t('fv.backMail') : t('fv.backJob');
  const pick = (i: number) => { setFi(i); setZoom(100); setRot(0); };
  const ask = (qq: string) => setLog((l) => [...l, [true, qq], [false, askAnswer(qq, kind)]]);

  const cells = (r: readonly string[]) => r.map((v, j) => (
    <span key={j} style={{ width: OFF_HEAD[j]![1], textAlign: OFF_HEAD[j]![2] }}>{v}</span>
  ));
  const offRows = sheet === 'PL' ? OFF_PL : OFF_CONT;

  const MERGED_PG: PdfPage[] = [
    { head: 'PHOTO · PTI', sub: 'Ảnh gộp từ IMG_2105.jpg', right: 'A4', rows: [], para: ['(Ảnh khổ A4, vừa khít trang)'] },
    { head: 'LỆNH HẠ CONT', sub: 'Lenh_ha_cont_884512.pdf', right: '', rows: [['Booking', 'YMLU-SGN-884512'], ['Cảng hạ', 'Cát Lái']], para: [] },
  ];
  const viewPages: [PdfPage, number][] = signing ? pages.map((pg) => [pg, 0]) : saved
    ? saved.pages.map((x) => [x.src >= 100 ? MERGED_PG[(x.src - 100) % 2]! : pages[x.src]!, x.rot])
    : pages.map((pg) => [pg, 0]);
  const body = (() => {
    if (pdfK) return viewPages.map(([pg, rotP], i) => <Paper key={`${pg.head}${i}`} pg={pg} no={i + 1} total={viewPages.length} name={name} z={zoom} rot={rotP} stamp={signing ? (i === sg.page ? sg : undefined) : signed && i === signed.page ? signed : undefined} />);
    if (kind === 'image') {
      return (
        <div role="img" aria-label={t('fv.ptiAlt')} className={s.img} style={{ width: Math.round((wide ? 900 : 680) * zoom / 100), height: Math.round((wide ? 620 : 470) * zoom / 100), transform: `rotate(${rot}deg)` }}>
          <div className={s.imgScr}><span>SET −18,0 °C</span><span>SUPPLY −18,2 °C</span></div>
          <div className={s.imgCont}>WHLU 552301 8</div>
        </div>
      );
    }
    if (kind === 'gallery') {
      const from = Math.max(0, Math.min(GAL_N - 10, gi - 4));
      return (
        <>
          <div className={s.galRow}>
            <button type="button" className={s.galNav} aria-label={t('fv.prev')} onClick={() => setGi(Math.max(0, gi - 1))}>‹</button>
            <div role="img" aria-label={gal.n} className={s.galImg} style={{ width: Math.round((wide ? 880 : 640) * zoom / 100), height: Math.round((wide ? 600 : 460) * zoom / 100), background: `linear-gradient(135deg, ${GAL_COLORS[gi % 6]}, ${GAL_COLORS[(gi + 2) % 6]})` }}><span>{gal.sc}</span></div>
            <button type="button" className={s.galNav} aria-label={t('fv.next')} onClick={() => setGi(Math.min(GAL_N - 1, gi + 1))}>›</button>
          </div>
          <div role="listbox" aria-label={t('fv.film')} className={s.film}>
            {Array.from({ length: 10 }, (_, k) => from + k).map((i) => (
              <button key={i} type="button" role="option" aria-selected={i === gi} aria-label={galItem(i).n} className={s.thumb} style={{ background: `linear-gradient(135deg, ${GAL_COLORS[i % 6]}, ${GAL_COLORS[(i + 2) % 6]})` }} onClick={() => setGi(i)} />
            ))}
          </div>
        </>
      );
    }
    if (kind === 'office') {
      return (
        <div className={s.office} style={{ width: wide ? 900 : 760 }}>
          <div className={s.sheets}>
            {([['PL', 'Packing list'], ['CONT', 'Container']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={sheet === k} onClick={() => setSheet(k)}>{l}</button>)}
          </div>
          <div role="table" aria-label="Packing list">
            <div role="row" className={`${s.orow} ${s.ohead}`}>{OFF_HEAD.map(([k, w, al]) => <span key={k} style={{ width: w, textAlign: al }}>{k}</span>)}</div>
            {offRows.map((r) => <div key={r[0]} role="row" className={`${s.orow} ${r[0] === 'Tổng' ? s.otot : ''}`}>{cells(r)}</div>)}
          </div>
        </div>
      );
    }
    return (
      <div className={s.text} style={{ width: wide ? 900 : 760 }}>
        {TXT.map((l, i) => <div key={i}><span>{i + 1}</span><span>{l}</span></div>)}
      </div>
    );
  })();

  return (
    <Page kind="tool">
      <Modal open={dlg === 'rename'} onOpenChange={(o) => !o && setDlg(null)} title={t('fv.renameT')}
        footer={<><Button variant="ghost" onClick={() => setDlg(null)}>{t('fv.cancel')}</Button><Button variant="primary" onClick={dlgDone}>{t('fv.saveName')}</Button></>}>
        <Input label={t('fv.fileName')} defaultValue={title} />
      </Modal>
      <Modal open={dlg === 'mail'} onOpenChange={(o) => !o && setDlg(null)} title={t('fv.mailT')} description={t('fv.mailFrom', { m: kind === 'mail' ? 'info@hanalogisticsvn.com' : 'support@hanalogisticsvn.com' })}
        footer={<><Button variant="ghost" onClick={() => setDlg(null)}>{t('fv.cancel')}</Button><Button variant="primary" onClick={() => (mTo.trim() ? dlgDone() : undefined)}>{t('fv.send')}</Button></>}>
        <Input label={t('fv.to')} value={mTo} onChange={(e) => setMTo(e.target.value)} error={mTo.trim() ? undefined : t('fv.toReq')} />
        <Input label="Cc" defaultValue="ma•••@hanalogisticsvn.com" />
        <Input label={t('fv.subject')} defaultValue={title} />
        <Textarea label={t('fv.body')} rows={4} defaultValue={t('fv.mailBody', { j: F.job || '' })} />
      </Modal>
      <Modal open={dlg === 'merge'} onOpenChange={(o) => !o && setDlg(null)} title={t('fv.mergeT')} description={t('fv.mergeD')}
        footer={<><Button variant="ghost" onClick={() => setDlg(null)}>{t('fv.cancel')}</Button><Button variant="primary" onClick={doMerge}>{t('fv.mergeN', { n: [0, 1].filter((i) => !mOff[i]).length })}</Button></>}>
        {([['IMG_2105.jpg', t('fv.mgPti')], ['Lenh_ha_cont_884512.pdf', t('fv.mgDrop')]] as const).map(([k, v], i) => (
          <label key={k}><input type="checkbox" checked={!mOff[i]} onChange={() => setMOff({ ...mOff, [i]: !mOff[i] })} /> <b>{k}</b> · {v}</label>
        ))}
        <Button variant="ghost" onClick={noWrite}>{t('fv.pickLocal')}</Button>
      </Modal>
      <LeaveModal open={leave} n={editor.ed.ops.length} onStay={() => setLeave(false)} onDiscard={() => { setLeave(false); setMode('view'); editor.reset(); }} />
      <div className={s.root}>
        <header className={s.bar}>
          <a href={kind === 'mail' ? '/box' : '/ezy/jobs'} className={s.back} aria-label={backK} title={backK}><IconArrowLeft size={18} aria-hidden="true" /></a>
          <span aria-hidden="true" className={`${s.badge} ${BADGE_CLS[bd]}`}>{bd}</span>
          <div className={s.titleBox}>
            <h1 className={s.h1}>{title}</h1>{signed && !editing && !signing && <Chip tone="info">{t('fv.viewing', { f: signedName })}</Chip>}{saved && !signed && !editing && !signing && <Chip tone="info">{t('fv.viewing', { f: `${name.replace(/\.pdf$/i, '')}_edited.pdf` })}</Chip>}
            <div className={s.meta} title={meta.map((m) => m[1]).join(' · ')}>
              {meta.map(([Ic, v, tip]) => <span key={tip} title={tip}><Ic size={14} aria-hidden="true" />{v}</span>)}
            </div>
          </div>
          {ready && editing && <EditorBar e={editor} onCancel={edCancel} onSave={edSave} />}
          {ready && signing && <SignBar page={sg.page} onCancel={() => setMode('view')} onApply={sigApply} />}
          {ready && !editing && !signing && (
            <>
              {(pdfK ? [...GROUPS, 'pdf' as Grp] : [...GROUPS]).concat(inJob ? ['job' as Grp] : []).map((g) => (
                <div key={g} className={s.grp}>
                  <button type="button" className={s.gBtn} aria-haspopup="menu" aria-expanded={openG === g} onClick={() => setOpenG(openG === g ? null : g)}>{t(`fv.g.${g}`)}<IconChevronDown size={14} aria-hidden="true" /></button>
                  {openG === g && (
                    <div role="menu" aria-label={t(`fv.g.${g}`)} className={s.menu}>
                      {MENUS[g].map((m) => (
                        <button key={m} type="button" role="menuitem" className={s.mi} onClick={() => menuPick(g, m)}>{t(`fv.mn.${g}.${m}`)}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button variant="ask" icon={<IconSparkles size={15} />} onClick={() => { setSide(true); }}>{t('fv.askHana')}</Button>
              <button type="button" className={s.ib} aria-label={t('fv.infoPanel')} title={t('fv.infoPanel')} aria-pressed={info} onClick={() => setInfo(!info)}><IconInfoCircle size={18} aria-hidden="true" /></button>
              {!wide && <button type="button" className={s.ib} aria-label={t('fv.rightCol')} title={t('fv.rightCol')} aria-pressed={side} onClick={() => setSide(!side)}><IconLayoutSidebarRight size={18} aria-hidden="true" /></button>}
            </>
          )}
        </header>
        <div className={s.cols}>
          {ready && info && (
            <aside className={s.info} aria-label={t('fv.fileInfo')}>
              {job && (
                <section className={s.card}>
                  <div className={s.jobTop}><a href="/ezy/jobs" className={s.jobNo}>{F.job}</a><Chip tone={job.tone}>{job.st}</Chip></div>
                  <dl className={s.dl}>{job.rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
                </section>
              )}
              {kind === 'gallery' && (
                <section className={s.card}><a href="/ezy/files" className={s.jobNo}>{CTX.gallery.title}</a><div className={s.sub}>{t('fv.galSub', { n: GAL_N })}</div></section>
              )}
              <section className={s.card}>
                <h3 className={s.h3}>{t('fv.file')}</h3>
                <dl className={s.dl}>{fileRows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
              </section>
              {inJob && (
                <section className={`${s.card} ${s.sib}`}>
                  <h3 className={s.h3}>{t('fv.otherFiles', { n: JOBFILES.length })}</h3>
                  {JOBFILES.map((j, i) => {
                    const b = badge(j.n, 'pdf');
                    return (
                      <button key={j.n} type="button" className={s.sibBtn} aria-current={i === fi} onClick={() => pick(i)}>
                        <span aria-hidden="true" className={`${s.sk} ${BADGE_CLS[b]}`}>{b}</span>
                        <span className={s.sibTx}><span className={s.sibN}>{j.n}</span><span className={s.sibW}>{j.type} · {j.by}</span></span>
                        {j.locked && <IconLock size={13} aria-label={t('fv.noDelete')} />}
                      </button>
                    );
                  })}
                </section>
              )}
            </aside>
          )}
          <main className={s.main}>
            {ready && kind === 'mail' && (
              <div className={s.mailStrip}>
                <IconMail size={16} aria-hidden="true" />
                <div className={s.mailTx}><b>{t('fv.fromMail')}</b> · ONE Customer Service · 22/Oct/2026 08:15<div className={s.sub}>{t('fv.notSaved')}</div></div>
                <a href="/box" className={s.mailGo}>{t('fv.openMail')}</a>
              </div>
            )}
            {ready && !editing && !signing && (
              <div className={s.zbar}>
                {hasPages && <span className={s.pageK}>{t('fv.pagesRange', { n: viewPages.length })}</span>}
                <button type="button" aria-label={t('fv.zoomOut')} className={s.zb} onClick={() => setZoom(Math.max(50, zoom - 10))}>−</button>
                <span className={s.zk}>{zoom}%</span>
                <button type="button" aria-label={t('fv.zoomIn')} className={s.zb} onClick={() => setZoom(Math.min(200, zoom + 10))}>+</button>
                <button type="button" className={s.zfit} onClick={() => setZoom(100)}><IconMaximize size={15} aria-hidden="true" />{t('fv.fit')}</button>
                {(kind === 'image' || kind === 'gallery') && <button type="button" className={s.zfit} aria-label={t('fv.rotate')} onClick={() => setRot((rot + 90) % 360)}><IconRotateClockwise size={15} aria-hidden="true" />{t('fv.rotateS')}</button>}
              </div>
            )}
            <div className={s.canvas}>
              <StateView
                state={state}
                title={state === 'error' ? t('fv.errT') : state === 'noperm' ? t('fv.nopermT') : t('fv.goneT')}
                desc={state === 'error' ? t('fv.errD') : state === 'noperm' ? t('fv.nopermD') : t('fv.goneD')}
                icon={state === 'noperm' ? <IconLock size={24} /> : <IconFileText size={24} />}
                action={state === 'error' ? <Button onClick={() => location.reload()}>{t('fv.reload')}</Button> : undefined}
                skeleton={<Skeleton rows={8} />}
              >
                {editing ? <PdfEditorBody e={editor} src={pages} name={name} onMerge={() => setDlg('merge')} onToast={(m) => toast({ msg: m })} /> : body}
              </StateView>
            </div>
          </main>
          {ready && signing && <SignPanel v={sg} onChange={setSg} n={pages.length} name={signedName} onToast={(m) => toast({ msg: m })} />}
          {showSide && !signing && (
            <aside className={s.side} aria-label={t('fv.rightCol')}>
              <div role="tablist" aria-label={t('fv.rightCol')} className={s.sTabs}>
                {([['ask', t('fv.askHana')], ['ocr', ocrHow]] as const).map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={sideTab === k} onClick={() => setSideTab(k)}>{l}</button>
                ))}
              </div>
              {sideTab === 'ask' ? (
                <>
                  <section className={s.askBox}>
                    <div className={s.askT}>{t('fv.readsFile')}</div>
                    <ul>{ASK[kind].map((x) => <li key={x}>{x}</li>)}</ul>
                  </section>
                  {log.map(([me, tx], i) => <div key={i} className={me ? s.me : s.bot}>{tx}</div>)}
                  <div className={s.chips}>{ASK_CHIPS.map((c) => <button key={c} type="button" onClick={() => ask(c)}>{c}</button>)}</div>
                  <form className={s.askIn} onSubmit={(e) => { e.preventDefault(); if (q.trim()) { ask(q.trim()); setQ(''); } }}>
                    <input aria-label={t('fv.askAbout')} placeholder={t('fv.askPh')} value={q} onChange={(e) => setQ(e.target.value)} />
                    <Button variant="primary" type="submit">{t('fv.ask')}</Button>
                  </form>
                </>
              ) : (
                <>
                  <div className={s.ocrHead}><b>{ocrHow}</b>{ocrRun && <span>{pdfK ? t('fv.pdfLayer', { n: pages.length }) : t('fv.ocrLang')}</span>}</div>
                  {ocrRun ? (
                    <>
                      <div className={s.ocrBox}>{(OCR[kind] ?? OCR.pdf).map((l) => <div key={l}>{l}</div>)}</div>
                      <div className={s.ocrAct}>
                        <button type="button" onClick={() => toast({ msg: t('fv.copied', { n: (OCR[kind] ?? OCR.pdf).length }) })}>{t('fv.copyS')}</button>
                        <button type="button" onClick={() => toast({ msg: t('fv.dlTxt', { f: `${name.replace(/\.[a-z]+$/i, '')}.txt` }) })}>{t('fv.dlTxtS')}</button>
                        <button type="button" onClick={() => { setSideTab('ask'); ask(t('fv.askExtract')); }}>{t('fv.askHana')}</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={s.sub}>{t('fv.notRun', { how: ocrHow })}</div>
                      <button type="button" className={s.ocrRun} onClick={() => setOcrRun(true)}>{t('fv.run', { how: ocrHow })}</button>
                    </>
                  )}
                </>
              )}
            </aside>
          )}
        </div>
      </div>
    </Page>
  );
}
